<script setup lang="ts">
// Space settings → Reports, for anyone with a moderation permission (HRTH-94). Acting on a
// report (hide the message, suspend or remove the sender) takes the action and records
// it, moving the report to "in review"; holders of "resolve reports" resolve it.
import { computed, ref, watch } from 'vue'
import type { ModerationKind, Report, ReportStatus, SpaceInfo } from '../api/types'
import { describeError } from '../api/http'
import { can, mayModerate } from '../lib/roles'
import { useSession } from '../stores/session'
import ReportCard from './ReportCard.vue'

const props = defineProps<{ space: SpaceInfo }>()
const session = useSession()
const reports = ref<Report[]>([])
const filter = ref<ReportStatus | 'all'>('open')
const busy = ref<string | null>(null)
const suspending = ref<string | null>(null)

async function load(): Promise<void> {
  try {
    reports.value = (await session.api.spaceReports(props.space.id)).reports
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
// The space changes when a report comes in or moves (a space hint): reload.
watch(() => props.space, load, { immediate: true })

const shown = computed(() => (filter.value === 'all' ? reports.value : reports.value.filter((r) => r.status === filter.value)))
const count = (s: ReportStatus) => reports.value.filter((r) => r.status === s).length

const member = (r: Report) => !!r.reported && props.space.members.some((m) => m.profile.user_id === r.reported)
const may = (r: Report, perm: 'hide_messages' | 'suspend_members' | 'remove_members') =>
  r.status !== 'resolved' &&
  member(r) &&
  !!session.me &&
  (perm !== 'hide_messages' || !!r.conversation) &&
  mayModerate(props.space, session.me, r.reported!, perm)

async function act(r: Report, kind: ModerationKind, minutes?: number): Promise<void> {
  busy.value = r.id
  suspending.value = null
  try {
    if (kind === 'hide') await session.hideMessage(r.conversation!, r.message_id!, true)
    else if (kind === 'suspend') await session.suspendMember(props.space.id, r.reported!, minutes!)
    else {
      if (!confirm('Remove them from the space?')) return
      await session.kick(props.space.id, r.reported!)
    }
    await session.api.reportAction(r.id, kind, minutes)
    await load()
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = null
  }
}

async function resolve(r: Report): Promise<void> {
  busy.value = r.id
  try {
    await session.api.resolveReport(r.id)
    await load()
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = null
  }
}

const LENGTHS = [
  { label: '1 hour', minutes: 60 },
  { label: '1 day', minutes: 24 * 60 },
  { label: '1 week', minutes: 7 * 24 * 60 },
  { label: '30 days', minutes: 30 * 24 * 60 },
]
</script>

<template>
  <section data-testid="reports-panel">
    <div class="mono mb-3 flex flex-wrap gap-1" role="tablist" aria-label="Report status">
      <button
        v-for="f in (['open', 'in_review', 'resolved', 'all'] as const)"
        :key="f"
        class="border px-2 py-1 uppercase"
        :class="filter === f ? 'border-ink text-text-primary' : 'border-border text-text-muted hover:border-border-strong'"
        role="tab"
        :aria-selected="filter === f"
        :data-testid="`reports-filter-${f}`"
        @click="filter = f"
      >
        {{ f === 'all' ? 'All' : f === 'in_review' ? `In review · ${count(f)}` : `${f} · ${count(f)}` }}
      </button>
    </div>
    <p v-if="!shown.length" class="mono py-4 text-text-muted" data-testid="reports-empty">Nothing here.</p>
    <div class="space-y-3">
      <ReportCard v-for="r in shown" :key="r.id" :report="r">
        <button v-if="may(r, 'hide_messages')" class="btn-outline h-7 px-2" :disabled="busy === r.id" data-testid="report-hide" @click="act(r, 'hide')">
          Hide message
        </button>
        <template v-if="may(r, 'suspend_members')">
          <button
            v-if="suspending !== r.id"
            class="btn-outline h-7 px-2"
            :disabled="busy === r.id"
            data-testid="report-suspend"
            @click="suspending = r.id"
          >
            Suspend…
          </button>
          <span v-else class="flex flex-wrap gap-1">
            <button
              v-for="l in LENGTHS"
              :key="l.minutes"
              class="btn-outline h-7 px-2"
              :data-testid="`report-suspend-${l.minutes}`"
              @click="act(r, 'suspend', l.minutes)"
            >
              {{ l.label }}
            </button>
          </span>
        </template>
        <button
          v-if="may(r, 'remove_members')"
          class="btn-outline h-7 px-2 text-error hover:text-error"
          :disabled="busy === r.id"
          data-testid="report-remove"
          @click="act(r, 'remove')"
        >
          Remove member
        </button>
        <button
          v-if="r.status !== 'resolved' && can(space, 'resolve_reports')"
          class="btn-primary h-7 px-2"
          :disabled="busy === r.id"
          data-testid="report-resolve"
          @click="resolve(r)"
        >
          Resolve
        </button>
      </ReportCard>
    </div>
  </section>
</template>
